import { Platform } from 'react-native';
import { compact } from 'lodash';
import * as XLSX from 'xlsx';

export type ExportSheet<T = { [key: string]: number | string }> = {
  sheet?: string;
  title?: string;
  jsonData: T[];
};

export const useExportTo = () => {
  const xlsxExport = (_fileName: string, _sheets: ExportSheet | ExportSheet[]): void => {
    if (Platform.OS !== 'web') return;

    const workbook = XLSX.utils.book_new();
    const isMultiSheet = Array.isArray(_sheets);
    const sheets = isMultiSheet ? _sheets : [_sheets];

    if (sheets.length) {
      sheets.forEach((_sheet, index) => {
        const startIndex = compact([_sheet.title, _sheet.jsonData]).length;
        const keys = Object.keys(_sheet.jsonData[0] || {});

        // @ts-ignore — origin typing not strict in xlsx
        const worksheet = XLSX.utils.json_to_sheet(_sheet.jsonData, { origin: `A${startIndex}` });
        const sheetName = _sheet.sheet ?? `Sheet${index + 1}`;

        if (_sheet.title) {
          XLSX.utils.sheet_add_aoa(worksheet, [[_sheet.title]], { origin: 'A1' });
          worksheet['A1'].s = {
            font: { bold: true, sz: 16, color: { rgb: '000000' } },
          };
        }

        const colWidths = (keys as string[]).map((key: string) => {
          const initialMax: number = key.length;
          const maxCellLen = (_sheet.jsonData as Record<string, unknown>[]).reduce((max: number, row: Record<string, unknown>): number => {
            const cell: unknown = row[key];
            const cellStr: string = cell == null ? '' : String(cell);
            return cellStr.length > max ? cellStr.length : max;
          }, initialMax);
          return { wch: maxCellLen + 2 };
        });
        worksheet['!cols'] = colWidths;

        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      });
    }

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { saveAs } = require('file-saver') as typeof import('file-saver');
    saveAs(blob, `${_fileName}.xlsx`);
  };

  return { xlsxExport };
};
